/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { InputAdornment, Paper, Stack } from '@mui/material';
import type { PickerValidDate } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import type { FC } from 'react';
import React from 'react';
import { dateUtils } from '@opengeoweb/shared';
import { CalendarToday } from '@opengeoweb/theme';
import { useTimesliderTranslation } from '../../../utils/i18n';

export interface MarkerTimeSelectorProps {
  value?: number; // Unix timestamp in seconds
  dataStartTime?: number;
  dataEndTime?: number;
  onSelect?: (value: number | null) => void;
  onCancel?: () => void;
  format?: string;
}

const getTimezoneOffsetS = (value: Date): number =>
  value.getTimezoneOffset() * 60;

const toLocalTime = (dateTime: number): Date =>
  dateUtils.fromUnix(
    dateTime + getTimezoneOffsetS(dateUtils.fromUnix(dateTime)),
  );

const toUTCTime = (dateTime: Date): number =>
  dateUtils.unix(dateTime) - getTimezoneOffsetS(dateTime);

const OpenPicker = (): React.ReactElement => <CalendarToday />;

interface DayStyle {
  borderWidth?: string;
  borderBottomStyle?: string;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
}

const enabledAndSelectedButtonStyle = {
  borderWidth: '6px',
  borderBottomStyle: 'solid',
  borderRadius: 0,
  borderColor: 'rgb(170, 210, 240)',
  backgroundColor: 'rgba(170, 210, 240, 0.3)',
};

export const enabledButUnSelectedButtonStyle = {
  ...enabledAndSelectedButtonStyle,
  backgroundColor: 'transparent',
};

export const disabledButtonStyle = {
  ...enabledAndSelectedButtonStyle,
  borderColor: 'transparent',
  backgroundColor: 'transparent',
};

const AvailableDayByTimeRange = (
  props: PickersDayProps,
  isDisabledStyle: boolean,
): React.ReactElement => {
  const { day, today, outsideCurrentMonth, ...other } = props;
  const [calendarDayStyle, setCalendarDayStyle] = React.useState<DayStyle>(
    {} as DayStyle,
  );

  React.useEffect(() => {
    if (outsideCurrentMonth || isDisabledStyle) {
      setCalendarDayStyle(disabledButtonStyle);
    } else if (today) {
      setCalendarDayStyle(enabledAndSelectedButtonStyle);
    } else {
      setCalendarDayStyle(enabledButUnSelectedButtonStyle);
    }
  }, [day, today, outsideCurrentMonth, isDisabledStyle]);

  return (
    <PickersDay
      {...other}
      day={day}
      disableHighlightToday={true}
      disableMargin={true}
      key={day.toString()}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={{ ...calendarDayStyle }}
    />
  );
};

export const MarkerTimeSelector: FC<MarkerTimeSelectorProps> = ({
  value,
  format = dateUtils.DATE_FORMAT_DATEPICKER,
  dataStartTime,
  dataEndTime,
  onSelect = (): void => {},
  onCancel = (): void => {},
}) => {
  const { t } = useTimesliderTranslation();

  const [localTimeValue, setLocalTimeValue] = React.useState<Date | null>(
    value ? toLocalTime(value) : null,
  );

  const [isUndefinedDateRange, setIsUndefinedDateRange] =
    React.useState<boolean>(
      !dataStartTime || !dataEndTime || dataStartTime > dataEndTime,
    );

  const isDateRangeDisabled = React.useCallback(
    (date: Date): boolean => {
      if (
        dataStartTime === undefined ||
        dataEndTime === undefined ||
        dataStartTime > dataEndTime
      ) {
        return true;
      }

      const startOfFirstDay = dateUtils.fromUnix(dataStartTime);
      startOfFirstDay.setHours(0, 0, 0);
      const endOfLastDay = dateUtils.fromUnix(dataEndTime);

      const day = dateUtils.unix(date);
      return (
        day < dateUtils.unix(startOfFirstDay) ||
        day > dateUtils.unix(endOfLastDay)
      );
    },
    [dataStartTime, dataEndTime],
  );

  const [isDisabledRange, setIsDisabledRange] = React.useState<boolean>(
    isDateRangeDisabled(localTimeValue!),
  );

  const handleSelect = (): void => {
    if (localTimeValue) {
      onSelect(toUTCTime(localTimeValue));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      handleSelect();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  };

  const isDisabledDateWithSomeAvailableData = (): boolean => {
    return isUndefinedDateRange ? false : isDisabledRange;
  };

  const handleDateChange = (value: PickerValidDate | null): void => {
    if (
      value instanceof Date ||
      value === null ||
      isDisabledDateWithSomeAvailableData()
    ) {
      setLocalTimeValue(value);
    }
  };

  const AvailableDay = (props: PickersDayProps): React.ReactElement => {
    return AvailableDayByTimeRange(props, isDateRangeDisabled(props!.day));
  };

  React.useEffect(() => {
    setIsUndefinedDateRange(
      !dataStartTime || !dataEndTime || dataStartTime > dataEndTime,
    );
    setIsDisabledRange(isDateRangeDisabled(localTimeValue!));
  }, [dataStartTime, dataEndTime, localTimeValue, isDateRangeDisabled]);

  return (
    <Paper sx={{ borderRadius: '0px', width: '240px' }}>
      <Stack data-testid="markerTimeSelector">
        <Paper
          sx={{
            height: '24px',
            align: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0px',
          }}
        >
          <Paper
            sx={{
              variant: 'subtitle',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              font: {
                fontWeight: 'medium',
                fontFamily: 'Roboto',
                fontSize: '12px',
              },

              borderRadius: '0px',
              boxShadow: 'none',
            }}
          >
            {t('timeslider-marker')}
          </Paper>
        </Paper>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            ampm={false}
            autoFocus={true}
            closeOnSelect={false}
            desktopModeMediaQuery="@media (min-width: 720px)"
            disableIgnoringDatePartForTimeValidation={true}
            format={format}
            label={t('timeslider-marker-label')}
            maxDateTime={
              isUndefinedDateRange ? undefined : toLocalTime(dataEndTime!)
            }
            minDateTime={
              isUndefinedDateRange ? undefined : toLocalTime(dataStartTime!)
            }
            onChange={handleDateChange}
            onClose={handleSelect}
            shouldDisableDate={isDisabledDateWithSomeAvailableData}
            slotProps={{
              textField: {
                variant: 'filled',
                InputProps: {
                  onKeyDownCapture: handleKeyDown,
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        paddingTop: '16px',
                      }}
                    >
                      UTC
                    </InputAdornment>
                  ),
                },
              },
              field: { openPickerButtonPosition: 'start' },
              openPickerButton: {
                size: 'small',
                sx: { padding: 0 },
              },
            }}
            slots={{
              openPickerIcon: OpenPicker,
              day: AvailableDay,
            }}
            sx={{
              marginTop: '8px',
              marginBottom: '8px',
              marginLeft: '8px',
              marginRight: '8px',
              borderRadius: '0px',
            }}
            timeSteps={{ minutes: 1 }}
            value={localTimeValue}
          />
        </LocalizationProvider>
      </Stack>
    </Paper>
  );
};
